import omit from 'lodash-es/omit';
import orderBy from 'lodash-es/orderBy';
import uniq from 'lodash-es/uniq';
import { IOption } from '../../components/Select';
import * as i from './lookupsInterfaces';

export function processItem<T>(
  items: T[] = [],
  defaultValues: i.IItem<T>,
  idFunc: (x: T) => number,
  nameFunc: (x: T) => string
): i.IItem<T> {
  const hash = items.reduce<i.INumHash<string>>(
    (newHash, item) => {
      const id = idFunc(item);
      const name = nameFunc(item);

      newHash[id] = name;
      return newHash;
    },
    { ...defaultValues.hash }
  );

  // get the new item ids
  const newItemIds = items.map(idFunc);
  // remove those ids from the initial items array
  const initCleaned = defaultValues.items.filter(x => !newItemIds.includes(idFunc(x)));

  return { items: [...initCleaned, ...items], hash };
}

export function processStringItem<T>(
  items: T[] = [],
  defaultValues: i.IStringItem<T>,
  idFunc: (x: T) => string,
  nameFunc: (x: T) => string
) : i.IStringItem<T> | any {
  if(items.length == 0)
  {
      return { items: [...defaultValues.items, ...items], ...defaultValues.hash };
  }
  else
  {
    const hash = items.reduce<i.IStringHash<string>>(
      (newHash, item) => {
        const id = idFunc(item);
        const name = nameFunc(item);

        newHash[id] = name;
        return newHash;
      },
      { ...defaultValues.hash }
    );

    // get the new item ids
    const newItemIds = items.map(x => idFunc(x));
    // remove those ids from the initial items array
    const initCleaned = defaultValues.items.filter(x => !newItemIds.includes(idFunc(x)));

    return { items: [...initCleaned, ...items], hash };
  }
}

export function processOptionsStringByNumId<T>(
  items: T[] = [],
  defaultValues: i.INumOptions<string>,
  idFunc: (x: T) => number,
  nameFunc: (x: T) => string
): i.INumOptions<string> {
  let options: IOption[] = [...defaultValues.options];
  const hash = items.reduce<i.INumHash<string>>(
    (newHash, item) => {
      const id = idFunc(item);
      const name = nameFunc(item);
      newHash[id] = name;
      options = [...options.filter(x => x.value !== id), { value: id, label: name }];
      return newHash;
    },
    { ...defaultValues.hash }
  );

  return { options: orderBy(options, (x: IOption) => x.value), hash };
}

export function processOptionsStringByStrId<T>(
  items: T[] = [],
  defaultValues: i.IStringOptions<string>,
  idFunc: (i: T) => string,
  nameFunc: (i: T) => string
): i.IStringOptions<string> {
  let options: IOption[] = [...defaultValues.options];
  const hash = items.reduce<i.IStringHash<string>>(
    (newHash, item) => {
      const id = idFunc(item);
      const name = nameFunc(item);
      newHash[id] = name;
      options = [...options.filter(x => x.value !== id), { value: id, label: name }];
      return newHash;
    },
    { ...defaultValues.hash }
  );

  return { options: orderBy(options, (x: IOption) => x.value), hash };
}

export function processString<T>(
  items: T[] = [],
  defaultValues: i.ILookup<string>,
  idFunc: (i: T) => number,
  nameFunc: (i: T) => string
): i.ILookup<string> {
  const ids: number[] = [...defaultValues.ids];
  const hash = items.reduce<i.INumHash<string>>(
    (newHash, item) => {
      const id = idFunc(item);
      const name = nameFunc(item);
      newHash[id] = name;
      ids.push(id);
      return newHash;
    },
    { ...defaultValues.hash }
  );
  return { ids: uniq(ids), hash };
}

export function processILookupObj<T>(
  items: T[] = [],
  defaultValues: i.ILookup<T>,
  idFunc: (f: T) => number
): i.ILookup<T> {
  const ids: number[] = [...defaultValues.ids];
  const hash = items.reduce<i.INumHash<T>>(
    (newHash, item) => {
      const id = idFunc(item);
      newHash[id] = item;
      ids.push(id);
      return newHash;
    },
    { ...defaultValues.hash }
  );
  return { ids: uniq(ids), hash };
}

export function processObjectStr<T>(
  items: T[] = [],
  defaultValues: i.IStringLookup<T>,
  idFunc: (x: T) => string
): i.IStringLookup<T> {
  const ids: string[] = [...defaultValues.ids];
  const hash = items.reduce<i.IStringHash<T>>(
    (newHash, item) => {
      const id = idFunc(item);
      newHash[id] = item;
      ids.push(id);
      return newHash;
    },
    { ...defaultValues.hash }
  );
  return { ids: uniq(ids), hash };
}

export function removeKey<T>(lookup: i.ILookup<T>, key: number): i.ILookup<T> {
  return {
    hash: omit(lookup.hash, key),
    ids: lookup.ids.filter(x => x !== key)
  };
}

export function processRemove<T>(value: i.IItem<T>, idToRemove: number, idFunc: (x: T) => number): i.IItem<T> {
  return {
    hash: omit(value.hash, idToRemove),
    items: value.items.filter(x => {
      const id = idFunc(x);

      return id !== idToRemove;
    })
  };
}
